'use client';

import { c as _c } from "react/compiler-runtime";
import { useInterval } from 'design-system/client';
import { pick } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import Amount, { AmountCSSProperties } from '../Amount';
import Comment, { CommentCSSProperties } from '../Comment';
import { composeSelectedFontObject, getCommentCssVariables, getDataCssVariables } from '../helpers';
import ScreenName from '../ScreenName';
import StreambotWrapper from '../StreambotWrapper';
import { StreambotComponentProps } from '../types';
import WidgetHeadline from '../WidgetHeadline';
import getDefaultValues from './getDefaultValues';
import type { TopDonationWidgetSettings } from './topDonationWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import { Maybe } from '@/types';
type Author = {
  name: Maybe<string>;
};
export type TopDonationData = {
  id: number;
  author: Maybe<Author>;
  donated_amount_in_cents?: number;
  message: Maybe<string>;
};
const defaultDemoData = [{
  id: 1,
  donated_amount_in_cents: 1500000,
  author: {
    name: 'Maxi M.'
  },
  message: 'Liebe an alle < 3'
}, {
  id: 2,
  donated_amount_in_cents: 1000000,
  author: {
    name: 'ChatKnight'
  },
  message: ''
}, {
  id: 3,
  donated_amount_in_cents: 777700,
  author: {
    name: 'StreamSage'
  },
  message: 'Ihr seid super! Freue mich jedes Jahr aufs Neue. Bitte weiter machen'
}] satisfies TopDonationData[];
type TopDonationProps = StreambotComponentProps<TopDonationWidgetSettings, TopDonationData[]>;
function useData(fundraisingEventId, t0, t1, demoData) {
  const $ = _c(11);
  const perPage = t0 === undefined ? 1 : t0;
  const maxCount = t1 === undefined ? 1 : t1;
  const [page, setPage] = useState(1);
  const [prevPerPage, setPrevPerPage] = useState(perPage);
  const locale = useLocale();
  let t2;
  if ($[0] !== demoData || $[1] !== page || $[2] !== perPage) {
    t2 = demoData?.slice((page - 1) * perPage, perPage * page);
    $[0] = demoData;
    $[1] = page;
    $[2] = perPage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const slicedDemoData = t2;
  let t3;
  if ($[4] !== locale) {
    t3 = {
      locale
    };
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      selector(param) {
        const data = param?.data;
        if (!data) {
          return null;
        }
        return data.map(_temp);
      },
      intervalMs: 100000
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const result = useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/opinions.json?order=amount_in_cents:DESC|id:ASC&per_page=${perPage}&page=${page}&facets=has_amount:true`, t3, t4, slicedDemoData);
  let t5;
  if ($[7] !== maxCount) {
    t5 = () => {
      setPage(previous => previous < maxCount ? previous + 1 : 1);
    };
    $[7] = maxCount;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const updatePage = t5;
  let t6;
  if ($[9] !== updatePage) {
    t6 = {
      callback: updatePage,
      intervalMs: 3000
    };
    $[9] = updatePage;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useInterval(t6);
  if (prevPerPage !== perPage) {
    setPage(1);
    setPrevPerPage(perPage);
  }
  return result;
}
function _temp(opinion) {
  return pick(opinion, ["author", "id", "donated_amount_in_cents", "message"]);
}
function DonationElement(t0) {
  const $ = _c(21);
  const {
    donation,
    amountCssVariables,
    commentCssVariables,
    screenNameFont,
    screenNameFontColor,
    screenNameFontSize,
    screenNameFontWeight,
    shouldShowComment,
    displayMode
  } = t0;
  if (!donation.id) {
    return null;
  }
  const t1 = displayMode === "list" ? "top-donation-list" : "top-donation-loop";
  const t2 = displayMode === "list" ? "amount-column" : undefined;
  let t3;
  if ($[0] !== amountCssVariables || $[1] !== donation.donated_amount_in_cents || $[2] !== t2) {
    t3 = <Amount amount={donation.donated_amount_in_cents} className={t2} style={amountCssVariables} />;
    $[0] = amountCssVariables;
    $[1] = donation.donated_amount_in_cents;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = donation.author?.name;
  const t5 = displayMode === "list" ? "screen-name-column" : undefined;
  let t6;
  if ($[4] !== screenNameFont || $[5] !== screenNameFontColor || $[6] !== screenNameFontSize || $[7] !== screenNameFontWeight || $[8] !== t4 || $[9] !== t5) {
    t6 = <ScreenName authorName={t4} screenNameFont={screenNameFont} screenNameFontColor={screenNameFontColor} screenNameFontSize={screenNameFontSize} screenNameFontWeight={screenNameFontWeight} className={t5} />;
    $[4] = screenNameFont;
    $[5] = screenNameFontColor;
    $[6] = screenNameFontSize;
    $[7] = screenNameFontWeight;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== commentCssVariables || $[12] !== displayMode || $[13] !== donation.message || $[14] !== shouldShowComment) {
    t7 = shouldShowComment && <Comment comment={donation.message} className={displayMode === "list" ? "comment-column" : undefined} style={commentCssVariables} />;
    $[11] = commentCssVariables;
    $[12] = displayMode;
    $[13] = donation.message;
    $[14] = shouldShowComment;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t1 || $[17] !== t3 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className={t1}>{t3}{t6}{t7}</div>;
    $[16] = t1;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function TopDonation(t0) {
  const $ = _c(87);
  let className;
  let fundraisingEventId;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      fundraisingEventId,
      demoData: t1,
      withDemoData: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = fundraisingEventId;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    fundraisingEventId = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const demoData = t1 === undefined ? defaultDemoData : t1;
  const withDemoData = t2 === undefined ? false : t2;
  let t3;
  if ($[6] !== props) {
    t3 = getDefaultValues(props);
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const mergedProps = t3;
  let commentFont;
  let commentFontColor;
  let commentFontSize;
  let commentFontWeight;
  let dataFont;
  let dataFontColor;
  let dataFontSize;
  let dataFontWeight;
  let displayAlignment;
  let displayMode;
  let displayNumber;
  let headlineFont;
  let headlineFontColor;
  let headlineFontSize;
  let headlineFontWeight;
  let headlineText;
  let screenNameFont;
  let screenNameFontColor;
  let screenNameFontSize;
  let screenNameFontWeight;
  let shouldShowComment;
  let wrapperProps;
  if ($[8] !== mergedProps) {
    ({
      displayMode,
      displayNumber,
      screenNameFont,
      screenNameFontColor,
      screenNameFontSize,
      screenNameFontWeight,
      dataFont,
      dataFontColor,
      dataFontSize,
      dataFontWeight,
      commentFont,
      commentFontColor,
      commentFontSize,
      commentFontWeight,
      shouldShowComment,
      headlineText,
      headlineFont,
      headlineFontWeight,
      headlineFontSize,
      headlineFontColor,
      displayAlignment,
      ...wrapperProps
    } = mergedProps);
    $[8] = mergedProps;
    $[9] = commentFont;
    $[10] = commentFontColor;
    $[11] = commentFontSize;
    $[12] = commentFontWeight;
    $[13] = dataFont;
    $[14] = dataFontColor;
    $[15] = dataFontSize;
    $[16] = dataFontWeight;
    $[17] = displayAlignment;
    $[18] = displayMode;
    $[19] = displayNumber;
    $[20] = headlineFont;
    $[21] = headlineFontColor;
    $[22] = headlineFontSize;
    $[23] = headlineFontWeight;
    $[24] = headlineText;
    $[25] = screenNameFont;
    $[26] = screenNameFontColor;
    $[27] = screenNameFontSize;
    $[28] = screenNameFontWeight;
    $[29] = shouldShowComment;
    $[30] = wrapperProps;
  } else {
    commentFont = $[9];
    commentFontColor = $[10];
    commentFontSize = $[11];
    commentFontWeight = $[12];
    dataFont = $[13];
    dataFontColor = $[14];
    dataFontSize = $[15];
    dataFontWeight = $[16];
    displayAlignment = $[17];
    displayMode = $[18];
    displayNumber = $[19];
    headlineFont = $[20];
    headlineFontColor = $[21];
    headlineFontSize = $[22];
    headlineFontWeight = $[23];
    headlineText = $[24];
    screenNameFont = $[25];
    screenNameFontColor = $[26];
    screenNameFontSize = $[27];
    screenNameFontWeight = $[28];
    shouldShowComment = $[29];
    wrapperProps = $[30];
  }
  let t4;
  if ($[31] !== headlineFont || $[32] !== headlineFontWeight) {
    t4 = composeSelectedFontObject(headlineFont, headlineFontWeight);
    $[31] = headlineFont;
    $[32] = headlineFontWeight;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  let t5;
  if ($[34] !== screenNameFont || $[35] !== screenNameFontWeight) {
    t5 = composeSelectedFontObject(screenNameFont, screenNameFontWeight);
    $[34] = screenNameFont;
    $[35] = screenNameFontWeight;
    $[36] = t5;
  } else {
    t5 = $[36];
  }
  let t6;
  if ($[37] !== dataFont || $[38] !== dataFontWeight) {
    t6 = composeSelectedFontObject(dataFont, dataFontWeight);
    $[37] = dataFont;
    $[38] = dataFontWeight;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  let t7;
  if ($[40] !== commentFont || $[41] !== commentFontWeight) {
    t7 = composeSelectedFontObject(commentFont, commentFontWeight);
    $[40] = commentFont;
    $[41] = commentFontWeight;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== t4 || $[44] !== t5 || $[45] !== t6 || $[46] !== t7) {
    t8 = [t4, t5, t6, t7];
    $[43] = t4;
    $[44] = t5;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  const selectedFonts = t8;
  let t9;
  if ($[48] !== selectedFonts) {
    t9 = {
      fonts: selectedFonts
    };
    $[48] = selectedFonts;
    $[49] = t9;
  } else {
    t9 = $[49];
  }
  useGoogleFonts(t9);
  const t = useTranslations("nextjs.streambot");
  const perPage = displayMode === "list" ? Number(displayNumber) : 1;
  const maxCount = displayMode === "list" ? 1 : Number(displayNumber);
  const {
    data
  } = useData(fundraisingEventId, perPage, maxCount, withDemoData ? demoData : undefined);
  let t10;
  if ($[50] !== dataFont || $[51] !== dataFontColor || $[52] !== dataFontSize || $[53] !== dataFontWeight) {
    t10 = getDataCssVariables(dataFont, dataFontSize, dataFontWeight, dataFontColor);
    $[50] = dataFont;
    $[51] = dataFontColor;
    $[52] = dataFontSize;
    $[53] = dataFontWeight;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  const amountCssVariables = t10;
  let t11;
  if ($[55] !== commentFont || $[56] !== commentFontColor || $[57] !== commentFontSize || $[58] !== commentFontWeight) {
    t11 = getCommentCssVariables(commentFont, commentFontSize, commentFontWeight, commentFontColor);
    $[55] = commentFont;
    $[56] = commentFontColor;
    $[57] = commentFontSize;
    $[58] = commentFontWeight;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  const commentCssVariables = t11;
  if (!data) {
    return null;
  }
  let t12;
  if ($[60] !== displayAlignment) {
    t12 = {
      textAlign: displayAlignment
    };
    $[60] = displayAlignment;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  let t13;
  if ($[62] !== headlineFont || $[63] !== headlineFontColor || $[64] !== headlineFontSize || $[65] !== headlineFontWeight || $[66] !== headlineText) {
    t13 = <WidgetHeadline content={headlineText} headlineFont={headlineFont} headlineFontWeight={headlineFontWeight} headlineFontSize={headlineFontSize} headlineFontColor={headlineFontColor} />;
    $[62] = headlineFont;
    $[63] = headlineFontColor;
    $[64] = headlineFontSize;
    $[65] = headlineFontWeight;
    $[66] = headlineText;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  let t14;
  if ($[68] !== amountCssVariables || $[69] !== commentCssVariables || $[70] !== data || $[71] !== displayMode || $[72] !== screenNameFont || $[73] !== screenNameFontColor || $[74] !== screenNameFontSize || $[75] !== screenNameFontWeight || $[76] !== shouldShowComment || $[77] !== t) {
    t14 = !data.length ? <div style={amountCssVariables} className="listAmount">{t("placeholder")}</div> : data.map(donation => <DonationElement key={donation.id} donation={donation} amountCssVariables={amountCssVariables} commentCssVariables={commentCssVariables} screenNameFont={screenNameFont} screenNameFontColor={screenNameFontColor} screenNameFontSize={screenNameFontSize} screenNameFontWeight={screenNameFontWeight} shouldShowComment={shouldShowComment} displayMode={displayMode} />);
    $[68] = amountCssVariables;
    $[69] = commentCssVariables;
    $[70] = data;
    $[71] = displayMode;
    $[72] = screenNameFont;
    $[73] = screenNameFontColor;
    $[74] = screenNameFontSize;
    $[75] = screenNameFontWeight;
    $[76] = shouldShowComment;
    $[77] = t;
    $[78] = t14;
  } else {
    t14 = $[78];
  }
  let t15;
  if ($[79] !== t12 || $[80] !== t13 || $[81] !== t14) {
    t15 = <div className="truncate-with-ellipsis align-heading" style={t12}>{t13}{t14}</div>;
    $[79] = t12;
    $[80] = t13;
    $[81] = t14;
    $[82] = t15;
  } else {
    t15 = $[82];
  }
  let t16;
  if ($[83] !== className || $[84] !== t15 || $[85] !== wrapperProps) {
    t16 = <StreambotWrapper {...wrapperProps} className={className}>{t15}</StreambotWrapper>;
    $[83] = className;
    $[84] = t15;
    $[85] = wrapperProps;
    $[86] = t16;
  } else {
    t16 = $[86];
  }
  return t16;
}
export default TopDonation;